import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getSupabaseClient, hasSupabaseConfig } from '../lib/supabaseClient';

const AuthContext = createContext(null);

const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_EMAIL_KEY = 'auth_email';

function toAuthenticatedUser(session) {
  if (!session?.access_token || !session?.user) {
    return null;
  }

  return {
    session,
    token: session.access_token,
    user: session.user,
  };
}

function persistLegacyAuth(session) {
  if (session?.access_token && session?.user?.email) {
    localStorage.setItem(AUTH_TOKEN_KEY, session.access_token);
    localStorage.setItem(AUTH_EMAIL_KEY, session.user.email);
    return;
  }

  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_EMAIL_KEY);
}

export const AuthProvider = ({ children }) => {
  const [authenticatedUser, setAuthenticatedUser] = useState(undefined);
  const [authError, setAuthError] = useState('');
  const [isProvisioningUser, setIsProvisioningUser] = useState(false);

  const applySession = useCallback((session) => {
    persistLegacyAuth(session);
    setAuthenticatedUser(toAuthenticatedUser(session));
  }, []);

  const initializeUser = useCallback(async (session) => {
    if (!session?.access_token) {
      return null;
    }

    setIsProvisioningUser(true);

    try {
      const response = await fetch('/.netlify/functions/initialize-user', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload?.message || payload?.error || 'Failed to finish setting up your account.');
      }

      return payload;
    } finally {
      setIsProvisioningUser(false);
    }
  }, []);

  const syncSession = useCallback(async (session, { initialize = true } = {}) => {
    if (!session?.access_token) {
      applySession(null);
      return null;
    }

    const supabase = getSupabaseClient();
    const { data, error } = await supabase.auth.getUser(session.access_token);

    if (error || !data?.user) {
      applySession(null);
      throw error || new Error('Unable to load your account.');
    }

    const normalizedSession = {
      ...session,
      user: data.user,
    };

    applySession(normalizedSession);

    if (initialize) {
      await initializeUser(normalizedSession);
    }

    applySession(normalizedSession);
    return toAuthenticatedUser(normalizedSession);
  }, [applySession, initializeUser]);

  useEffect(() => {
    if (!hasSupabaseConfig()) {
      setAuthError('Supabase auth is not configured for this environment.');
      setAuthenticatedUser(null);
      return undefined;
    }

    let isMounted = true;
    const supabase = getSupabaseClient();

    const restoreSession = async () => {
      try {
        setAuthError('');
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          throw error;
        }

        if (!isMounted) {
          return;
        }

        await syncSession(data.session, { initialize: true });
      } catch (error) {
        console.error('Failed to restore auth session:', error);

        if (isMounted) {
          setAuthError('We could not restore your session. Please sign in again.');
          applySession(null);
        }
      }
    };

    restoreSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      syncSession(session, { initialize: Boolean(session?.access_token) }).catch((error) => {
        console.error('Failed to sync auth session:', error);
        setAuthError('We could not finish signing you in. Please try again.');
      });
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [applySession, syncSession]);

  const requestOtp = useCallback(async (email) => {
    setAuthError('');
    const supabase = getSupabaseClient();

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
      },
    });

    if (error) {
      throw error;
    }
  }, []);

  const verifyOtp = useCallback(async ({ email, token }) => {
    setAuthError('');
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    });

    if (error) {
      throw error;
    }

    if (!data?.session?.access_token) {
      throw new Error('No session returned from verification.');
    }

    return syncSession(data.session, { initialize: true });
  }, [syncSession]);

  const signOut = useCallback(async () => {
    setAuthError('');

    if (!hasSupabaseConfig()) {
      applySession(null);
      return;
    }

    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.signOut();

    applySession(null);

    if (error) {
      throw error;
    }
  }, [applySession]);

  const value = useMemo(() => ({
    authenticatedUser,
    authError,
    authLoading: authenticatedUser === undefined || isProvisioningUser,
    hasSupabaseConfig: hasSupabaseConfig(),
    requestOtp,
    resendOtp: requestOtp,
    signOut,
    verifyOtp,
  }), [
    authenticatedUser,
    authError,
    isProvisioningUser,
    requestOtp,
    signOut,
    verifyOtp,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
